// Theme resolution and the toggle control.
//
// The initial value is applied by an inline script in index.html so there's no
// flash of the wrong theme before this module loads. This file must resolve
// identically — if you change one, change the other.

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'portfolio.theme';

function systemTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function storedTheme(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    // Safari in private mode throws on localStorage access rather than
    // returning null. A theme preference isn't worth breaking the page over.
    return null;
  }
}

export function currentTheme(): Theme {
  const explicit = document.documentElement.getAttribute('data-theme');
  if (explicit === 'light' || explicit === 'dark') return explicit;
  return storedTheme() ?? systemTheme();
}

export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Preference just won't persist; the page still works.
  }
}

/**
 * Follow the OS while the visitor has never chosen explicitly. Once they pick,
 * their choice sticks and the OS stops overriding it.
 */
export function followSystemUntilChosen(onChange: (theme: Theme) => void): void {
  if (storedTheme() !== null) return;
  window.matchMedia('(prefers-color-scheme: light)').addEventListener('change', (event) => {
    if (storedTheme() !== null) return;
    const next: Theme = event.matches ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    onChange(next);
  });
}

const SUN = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>`;
const MOON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z"/></svg>`;

function labelFor(theme: Theme): string {
  return theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme';
}

function paint(button: HTMLButtonElement, theme: Theme): void {
  // Show the theme you'd get by clicking, which is the opposite of the current
  // one — a sun on a dark page.
  button.innerHTML = theme === 'dark' ? SUN : MOON;
  button.setAttribute('aria-label', labelFor(theme));
  button.setAttribute('title', labelFor(theme));
}

/** Builds the toggle and keeps it in sync with theme changes from any source. */
export function createThemeToggle(): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'rl-theme-toggle';
  paint(button, currentTheme());

  button.addEventListener('click', () => {
    const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    paint(button, next);
  });

  followSystemUntilChosen((theme) => paint(button, theme));
  return button;
}
